// hooks/useInactivityLogout.ts
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { parseJwt } from "@/utils/parse-jwt";

const INACTIVITY_LIMIT = 60 ** 2 * 1000;
const TOKEN_CHECK_INTERVAL = 30 * 1000;
const TOKEN_KEY = "@App:token";
const PUBLIC_PATHS = ["/signin", "/signup", "/forgot-password", "/reset-password"];

type LogoutReason = "inactivity" | "no_token" | undefined;

export function useInactivityLogout() {
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const tokenCheckInterval = useRef<ReturnType<typeof setInterval>>();
  const isLoggingOutRef = useRef(false);
  const location = useLocation();

  const isPainelPage = location.pathname.startsWith("/painel/");
  const isPublicPage = PUBLIC_PATHS.some((path) => location.pathname.startsWith(path));
  const shouldSkip = isPainelPage || isPublicPage;

  const logout = (reason?: LogoutReason) => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    clearTimeout(timer.current);
    clearInterval(tokenCheckInterval.current);
    localStorage.clear();

    if (reason) {
      window.location.replace(`/signin?logout_reason=${reason}`);
    } else {
      window.location.replace(`/signin`);
    }
  };

  const isTokenExpired = (token: string) => {
    const exp = parseJwt(token)?.exp;
    return !!exp && exp * 1000 < Date.now();
  };

  const hasToken = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;

    if (isTokenExpired(token)) {
      logout();
      return false;
    }

    return true;
  };

  const resetTimer = () => {
    if (shouldSkip) return;

    if (!hasToken()) {
      logout("no_token");
      return;
    }

    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      logout("inactivity");
    }, INACTIVITY_LIMIT);
  };

  useEffect(() => {
    if (shouldSkip) return;

    if (!hasToken()) {
      logout("no_token");
      return;
    }

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];

    events.forEach((event) => window.addEventListener(event, resetTimer));
    resetTimer();

    tokenCheckInterval.current = setInterval(() => {
      if (!hasToken()) {
        logout("no_token");
      }
    }, TOKEN_CHECK_INTERVAL);

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer));
      clearTimeout(timer.current);
      clearInterval(tokenCheckInterval.current);
    };
  }, [shouldSkip]);
}
